import { Routes } from '@common/constants';
import { useAppContext } from '@common/contexts/AppContext';
import { useLabels } from '@features/Menu/useLabels';
import { useModuleStatuses, usePropertyModules } from '@features/properties';
import { useRatePageSettings } from '@features/rates';
import { useRoomPageSettings } from '@features/rooms';
import {
  useGetSpaClassesPage,
  useGetSpaDaysPage,
  useGetSpaFacilitiesPage,
  useGetSpaTreatmentsPage,
} from '@features/spa/hooks';
import { useGetTablesPage } from '@features/tables/hooks/useGetTablesPage';
import { useTranslations } from '@lang';
import { entitySelectors } from '@store/entity/selectors';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useRouter } from 'shared-routing';
import type { Property } from 'shared-types/Property';
import type { Product } from 'shared-types/Product';
import type { CategoryItem } from 'voyager';
import { buildCardImageUrl } from '@utils/buildCardImageUrl';
import { entersSearchFlow, getCategoryListRoute } from '../SearchFlow';
import { useFeaturedModuleVisibility } from '../useFeaturedModuleVisibility';

interface UseHomeCategoriesArgs {
  property: Pick<
    Property,
    'id' | 'name' | 'isTablesIntegrated' | 'isRetailEnabled' | 'modulesExcludingDisabled'
  >;
  language: string;
}

interface UseHomeCategoriesResult {
  categories: CategoryItem[];
  selectCategory: (id: string) => void;
}

export function useHomeCategories({
  property,
  language,
}: UseHomeCategoriesArgs): UseHomeCategoriesResult {
  const router = useRouter();
  const { t } = useTranslations();
  const labels = useLabels();
  const { siteId } = useAppContext();

  const { data: modules } = usePropertyModules(siteId);
  const { isFeaturedSpaDaysVisible, isFeaturedSpaTreatmentsVisible } =
    useFeaturedModuleVisibility();

  const {
    statuses: { isRoomsEnabled },
  } = useModuleStatuses();

  const isSpaEnabled = modules?.spa?.enabled ?? false;
  const isSpaStaysVisible =
    property?.modulesExcludingDisabled?.spa?.features?.stays?.enabled ?? false;
  const isClassesVisible = isSpaEnabled && (modules?.spa?.features?.classes?.enabled ?? false);
  const isFacilitiesVisible =
    isSpaEnabled && (modules?.spa?.features?.facilities?.enabled ?? false);
  const isRetailVisible = property?.isRetailEnabled;

  const { content: roomsPage } = useRoomPageSettings(language, property?.name);
  const { content: ratesPage } = useRatePageSettings(language, property?.name);
  const { data: spaDaysPage } = useGetSpaDaysPage(property?.id, language);
  const { data: spaTreatmentsPage } = useGetSpaTreatmentsPage(property?.id, language);
  const { data: spaClassesPage } = useGetSpaClassesPage(property?.id, language);
  const { data: spaFacilitiesPage } = useGetSpaFacilitiesPage(property?.id, language);
  const { data: tablesPage } = useGetTablesPage(property?.id, language);

  const products = useSelector(entitySelectors.getAllProducts) as Product[];
  const firstProductImageUrl = products?.[0]?.imageGallery?.length
    ? products[0].imageGallery[0]?.url
    : products?.[0]?.image?.url;
  const productsTileImageUrl = buildCardImageUrl(firstProductImageUrl);

  const categories = useMemo((): CategoryItem[] => {
    const items: CategoryItem[] = [];

    if (isRoomsEnabled) {
      items.push({
        id: 'rooms',
        label: labels?.rooms || t('flynav.rooms'),
        imageUrl: roomsPage?.heroBackgroundImage,
      });
    }

    if (isRoomsEnabled) {
      items.push({
        id: 'rates',
        label: labels?.rates || t('flynav.rates'),
        imageUrl: ratesPage?.heroBackgroundImage,
      });
    }

    if (property?.isTablesIntegrated) {
      items.push({
        id: 'tables',
        label: labels?.tables || t('flynav.dining'),
        imageUrl: tablesPage?.heroBackgroundImage?.url,
      });
    }

    if (isSpaStaysVisible) {
      items.push({
        id: 'spa-stays',
        label: labels?.spastays || t('flynav.spaStays'),
        imageUrl: ratesPage?.heroBackgroundImage,
      });
    }

    if (isFeaturedSpaDaysVisible) {
      items.push({
        id: 'spa-days',
        label: labels?.spadays || t('flynav.spaDays'),
        imageUrl: spaDaysPage?.heroBackgroundImage?.url,
      });
    }

    if (isFeaturedSpaTreatmentsVisible) {
      items.push({
        id: 'spa-treatments',
        label: labels?.treatments || t('flynav.treatments'),
        imageUrl: spaTreatmentsPage?.heroBackgroundImage?.url,
      });
    }

    if (isFacilitiesVisible) {
      items.push({
        id: 'facilities',
        label: labels?.facilities || t('app.facilities'),
        imageUrl: spaFacilitiesPage?.heroBackgroundImage?.url,
      });
    }

    if (isClassesVisible) {
      items.push({
        id: 'spa-classes',
        label: labels?.classes || t('flynav.classes'),
        imageUrl: spaClassesPage?.heroBackgroundImage?.url,
      });
    }

    if (isRetailVisible) {
      items.push({
        id: 'products',
        label: labels?.retail || t('flynav.shop'),
        imageUrl: productsTileImageUrl,
        imageOverlay: true,
      });
    }

    return items;
  }, [
    isRoomsEnabled,
    isSpaStaysVisible,
    isFeaturedSpaDaysVisible,
    isFeaturedSpaTreatmentsVisible,
    isClassesVisible,
    isFacilitiesVisible,
    isRetailVisible,
    productsTileImageUrl,
    property?.isTablesIntegrated,
    labels,
    t,
    roomsPage,
    ratesPage,
    spaDaysPage,
    spaTreatmentsPage,
    spaClassesPage,
    spaFacilitiesPage,
    tablesPage,
  ]);

  const selectCategory = useCallback(
    (id: string) => {
      if (entersSearchFlow(id)) {
        router.push({ pathname: Routes.SEARCH, query: { category: id, step: 'when' } });
        return;
      }
      const route = getCategoryListRoute(id);
      if (route) {
        router.push({ pathname: route });
      }
    },
    [router]
  );

  return { categories, selectCategory };
}
